import { useState } from 'react';
import { fetchAffiliates } from '../analytics.logic';
import { AffiliateReseller } from '../types/affiliateAnalytics.types';
import { downloadCSV, getLocalTimezone, stanNotify } from '@/lib';
import { SENTRY_FEATURES } from '@/lib/constants';

const EXPORT_PAGE_SIZE = 1000;

/**
 * Converts affiliate data to CSV format and triggers download
 */
const affiliatesToCSV = (affiliates: AffiliateReseller[]) => {
    const headers = [
        'Username',
        'Full Name',
        'Email',
        'Status',
        'Total Purchases',
        'Total Revenue',
        'Refunds / Disputes',
        'Custom Commission %',
    ];

    const rows = affiliates.map(affiliate => [
        affiliate.username,
        affiliate.full_name,
        affiliate.email,
        affiliate.resell_status,
        affiliate.total_sales,
        affiliate.total_earnings,
        affiliate.total_refunded_or_disputed,
        affiliate.affiliate_custom_percentage || '',
    ]);

    const today = new Date();
    const filename = `Your Stan Affiliates - ${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;

    downloadCSV({
        filename,
        headers,
        rows,
    });
};

export function useAffiliateCSVExport(pageId: string, backendFilterValues?: string) {
    const [isExporting, setIsExporting] = useState(false);

    /**
     * Exports all affiliate data to CSV
     * - Fetches all pages of data in parallel for performance
     * - Applies current filters to export
     * - Shows success notification with total count
     */
    const exportToCSV = async () => {
        if (!pageId) return;

        setIsExporting(true);
        try {
            // Fetch first page to determine total pages
            const firstPage = await fetchAffiliates(pageId, {
                page: 1,
                pageSize: EXPORT_PAGE_SIZE,
                filter_array: backendFilterValues || JSON.stringify([]),
                timezone: getLocalTimezone(),
            });

            const totalPages = firstPage.metadata.totalPages;

            // If only one page, use that data
            if (totalPages === 1) {
                affiliatesToCSV(firstPage.data);
                return;
            }

            // Fetch all remaining pages in parallel for better performance
            const pagePromises = Array.from({ length: totalPages - 1 }, (_, i) =>
                fetchAffiliates(pageId, {
                    page: i + 2,
                    pageSize: EXPORT_PAGE_SIZE,
                    filter_array: backendFilterValues || JSON.stringify([]),
                    timezone: getLocalTimezone(),
                })
            );

            const results = await Promise.all(pagePromises);

            // Consolidate all results
            const allAffiliates: AffiliateReseller[] = [...firstPage.data, ...results.flatMap(result => result.data)];
            affiliatesToCSV(allAffiliates);

            stanNotify({
                type: 'success',
                title: 'Export Complete',
                text: `Successfully exported ${allAffiliates.length} affiliates`,
                duration: 3000,
            });
        } catch (error) {
            stanNotify({
                type: 'error',
                title: 'Failed to export affiliates',
                text: 'Failed to export affiliates',
                duration: 3000,
                error: {
                    message: '[ANALYTICS] failed-to-export-affiliates',
                    options: {
                        tags: { feature: SENTRY_FEATURES.ANALYTICS },
                        context: { error },
                    },
                },
            });
        } finally {
            setIsExporting(false);
        }
    };

    return {
        exportToCSV,
        isExporting,
    };
}
